import { useEffect, useRef, useState } from "react";
import * as lamejs from "@breezystack/lamejs";
import { AudioPair } from "../../../types/type";
import { downsampleAudio } from "../../../module/audio/audio.util";

export type DualAudioState = {
  vocalUrl: string;
  mrUrl: string;
  transactionId: string;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  vocalGain: number;
  mrGain: number;
  waveformData?: Float32Array;
};

export const useDualAudio = () => {
  const [currentAudio, setCurrentAudio] = useState<DualAudioState | null>(null);

  // Audio Context and nodes
  const audioContext = useRef<AudioContext | null>(null);
  const vocalSourceNode = useRef<AudioBufferSourceNode | null>(null); // 보컬 재생 노드
  const mrSourceNode = useRef<AudioBufferSourceNode | null>(null); // mr 재생 노드
  const vocalGainNode = useRef<GainNode | null>(null); // 보컬 게인 조절용 노드
  const mrGainNode = useRef<GainNode | null>(null); // mr 게인 조절용 노드

  // Audio Buffers
  const audioBufferCache = useRef<Map<string, AudioBuffer>>(new Map());
  const vocalBuffer = useRef<AudioBuffer | null>(null);
  const mrBuffer = useRef<AudioBuffer | null>(null);

  // Playback tracking
  const startTime = useRef<number>(0);
  const pausedAt = useRef<number>(0);
  const isInitialized = useRef<boolean>(false);

  useEffect(() => {
    audioContext.current = new (window.AudioContext ||
      window.webkitAudioContext)();
    return () => {
      audioContext.current?.close();
    };
  }, []);

  const initializeAudioNodes = () => {
    if (!audioContext.current) return;

    // Create gain nodes
    vocalGainNode.current = audioContext.current.createGain();
    mrGainNode.current = audioContext.current.createGain();

    // Connect nodes to destination
    vocalGainNode.current.connect(audioContext.current.destination);
    mrGainNode.current.connect(audioContext.current.destination);
  };

  const loadAudioBuffer = async (url: string): Promise<AudioBuffer> => {
    if (!audioContext.current) throw new Error("AudioContext not initialized");

    // 캐시된 버퍼가 있으면 반환
    if (audioBufferCache.current.has(url)) {
      return audioBufferCache.current.get(url)!;
    }

    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const buffer = await audioContext.current.decodeAudioData(arrayBuffer);

    // 캐시에 저장
    audioBufferCache.current.set(url, buffer);
    return buffer;
  };

  const handlePlayAudioPair = async (
    audioPair: AudioPair,
    transactionId: string
  ) => {
    if (!audioContext.current) return;

    // If same audio is playing, handle pause/resume
    if (
      currentAudio?.transactionId === transactionId &&
      currentAudio?.vocalUrl === audioPair.vocalUrl &&
      currentAudio?.mrUrl === audioPair.mrUrl
    ) {
      if (currentAudio.isPlaying) {
        stopPlayback();
        setCurrentAudio((prev) =>
          prev ? { ...prev, isPlaying: false } : null
        );
      } else {
        resumePlayback();
        setCurrentAudio((prev) => (prev ? { ...prev, isPlaying: true } : null));
      }
      return;
    }

    // Stop current playback if different audio
    stopPlayback();

    // Reset playback position for new audio
    pausedAt.current = 0;
    startTime.current = 0;

    try {
      if (!isInitialized.current) {
        initializeAudioNodes();
        isInitialized.current = true;
      }

      [vocalBuffer.current, mrBuffer.current] = await Promise.all([
        loadAudioBuffer(audioPair.vocalUrl),
        loadAudioBuffer(audioPair.mrUrl),
      ]);

      startPlayback();

      setCurrentAudio({
        vocalUrl: audioPair.vocalUrl,
        mrUrl: audioPair.mrUrl,
        transactionId,
        currentTime: 0, // 명시적으로 0으로 설정
        duration: vocalBuffer.current.duration,
        isPlaying: true,
        vocalGain: 1,
        mrGain: 1,
      });
    } catch (error) {
      console.error("Error loading audio:", error);
    }
  };

  const startPlayback = () => {
    if (!audioContext.current || !vocalBuffer.current || !mrBuffer.current)
      return;

    // Create new source nodes
    vocalSourceNode.current = audioContext.current.createBufferSource();
    mrSourceNode.current = audioContext.current.createBufferSource();

    // Set buffers
    vocalSourceNode.current.buffer = vocalBuffer.current;
    mrSourceNode.current.buffer = mrBuffer.current;

    // Connect to gain nodes
    vocalSourceNode.current.connect(vocalGainNode.current!);
    mrSourceNode.current.connect(mrGainNode.current!);

    // Start playback
    const startOffset = pausedAt.current;
    startTime.current = audioContext.current.currentTime - startOffset;

    vocalSourceNode.current.start(0, startOffset);
    mrSourceNode.current.start(0, startOffset);

    // Update current time
    requestAnimationFrame(updatePlaybackTime);
  };

  const stopPlayback = () => {
    vocalSourceNode.current?.stop();
    mrSourceNode.current?.stop();
    pausedAt.current = audioContext.current?.currentTime! - startTime.current;
  };

  const resumePlayback = () => {
    startPlayback();
  };

  const updatePlaybackTime = () => {
    if (!currentAudio?.isPlaying || !audioContext.current) return;

    const currentTime = audioContext.current.currentTime - startTime.current;
    setCurrentAudio((prev) => (prev ? { ...prev, currentTime } : null));

    if (currentTime < currentAudio.duration) {
      requestAnimationFrame(updatePlaybackTime);
    }
  };

  const handleTimeUpdate = (value: number, transactionId: string) => {
    if (currentAudio?.transactionId !== transactionId) return;

    stopPlayback();
    pausedAt.current = value;
    if (currentAudio.isPlaying) {
      startPlayback();
    }
  };

  const handleSkipTime = (seconds: number, transactionId: string) => {
    if (currentAudio?.transactionId !== transactionId) return;

    const newTime = Math.min(
      Math.max(pausedAt.current + seconds, 0),
      currentAudio.duration
    );
    handleTimeUpdate(newTime, transactionId);
  };

  const mergeAudios = async (
    editedVocalUrl: string,
    mrUrl: string
  ): Promise<Blob> => {
    const [vocalBuffer, mrBuffer] = await Promise.all([
      loadAudioBuffer(editedVocalUrl),
      loadAudioBuffer(mrUrl),
    ]);

    // Create offline context for rendering
    const offlineCtx = new OfflineAudioContext(
      2, // stereo
      vocalBuffer.length,
      vocalBuffer.sampleRate
    );

    // Create and connect source nodes
    const vocalSource = offlineCtx.createBufferSource();
    const mrSource = offlineCtx.createBufferSource();
    vocalSource.buffer = vocalBuffer;
    mrSource.buffer = mrBuffer;

    // Create merger node for stereo output
    const merger = offlineCtx.createChannelMerger(2);
    vocalSource.connect(merger, 0, 0);
    mrSource.connect(merger, 0, 1);
    merger.connect(offlineCtx.destination);

    // Start sources
    vocalSource.start(0);
    mrSource.start(0);

    // Render audio
    const renderedBuffer = await offlineCtx.startRendering();

    // Convert to MP3
    const mp3Data = audioBufferToMp3(renderedBuffer);
    return new Blob([mp3Data], { type: "audio/mp3" });
  };

  const handleTrimAudio = (
    start: number,
    end: number,
    mode: "cut" | "mute" = "mute"
  ): AudioPair | null => {
    if (!currentAudio || !audioContext.current) return null;
    try {
      const wasPlaying = currentAudio.isPlaying;
      if (wasPlaying) {
        stopPlayback();
      }

      // 버퍼 편집
      const editedVocalBuffer =
        mode === "cut"
          ? trimBuffer(vocalBuffer.current!, start, end)
          : muteBuffer(vocalBuffer.current!, start, end);

      // 2. 새로운 파형 데이터 생성
      const channelData = editedVocalBuffer.getChannelData(0);
      const newWaveformData = downsampleAudio(channelData, 800);

      vocalBuffer.current = editedVocalBuffer;
      if (wasPlaying) {
        startPlayback();
      }

      setCurrentAudio((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          duration: editedVocalBuffer.duration,
          waveformData: newWaveformData,
        };
      });

      if (wasPlaying) {
        startPlayback();
      }

      return {
        vocalUrl: currentAudio.vocalUrl, // URL은 그대로 유지
        mrUrl: currentAudio.mrUrl,
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  // const handleTrimAudio = async (
  //   start: number,
  //   end: number,
  //   mode: "cut" | "mute" = "mute"
  // ): Promise<AudioPair | null> => {
  //   if (!audioContext.current || !currentAudio) {
  //     console.error("AudioContext not initialized or no current audio");
  //     return null;
  //   }

  //   try {
  //     const vocalBuffer = await loadAudioBuffer(currentAudio.vocalUrl);
  //     const mrBuffer = await loadAudioBuffer(currentAudio.mrUrl);
  //     const sampleRate = vocalBuffer.sampleRate;
  //     const startOffset = Math.floor(start * sampleRate);
  //     const endOffset = Math.floor(end * sampleRate);

  //     if (mode === "cut") {
  //       // vocal과 mr 모두 자르기
  //       const newVocalBuffer = trimBuffer(vocalBuffer, startOffset, endOffset);
  //       const newMrBuffer = trimBuffer(mrBuffer, startOffset, endOffset);

  //       // vocal MP3 생성
  //       const vocalMp3Data = audioBufferToMp3(newVocalBuffer);
  //       const vocalBlob = new Blob([vocalMp3Data], { type: "audio/mp3" });
  //       const newVocalUrl = URL.createObjectURL(vocalBlob);

  //       // mr MP3 생성
  //       const mrMp3Data = audioBufferToMp3(newMrBuffer);
  //       const mrBlob = new Blob([mrMp3Data], { type: "audio/mp3" });
  //       const newMrUrl = URL.createObjectURL(mrBlob);

  //       // 이전 URL 정리
  //       if (currentAudio.vocalUrl.startsWith("blob:")) {
  //         URL.revokeObjectURL(currentAudio.vocalUrl);
  //       }
  //       if (currentAudio.mrUrl.startsWith("blob:")) {
  //         URL.revokeObjectURL(currentAudio.mrUrl);
  //       }

  //       // 캐시 업데이트
  //       audioBufferCache.current.set(newVocalUrl, newVocalBuffer);
  //       audioBufferCache.current.set(newMrUrl, newMrBuffer);

  //       return { vocalUrl: newVocalUrl, mrUrl: newMrUrl };
  //     } else {
  //       // mute 모드: vocal만 음소거
  //       const newVocalBuffer = muteBuffer(vocalBuffer, startOffset, endOffset);
  //       const mp3Data = audioBufferToMp3(newVocalBuffer);
  //       const blob = new Blob([mp3Data], { type: "audio/mp3" });

  //       if (currentAudio.vocalUrl.startsWith("blob:")) {
  //         URL.revokeObjectURL(currentAudio.vocalUrl);
  //       }

  //       const newVocalUrl = URL.createObjectURL(blob);
  //       audioBufferCache.current.set(newVocalUrl, newVocalBuffer);

  //       return { vocalUrl: newVocalUrl, mrUrl: currentAudio.mrUrl };
  //     }
  //   } catch (error) {
  //     console.error("Audio trim failed:", error);
  //     return null;
  //   }
  // };

  // 버퍼 트리밍 헬퍼 함수
  const trimBuffer = (
    buffer: AudioBuffer,
    startOffset: number,
    endOffset: number
  ): AudioBuffer => {
    const newLength = buffer.length - (endOffset - startOffset);
    const newBuffer = audioContext.current!.createBuffer(
      buffer.numberOfChannels,
      newLength,
      buffer.sampleRate
    );

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      const newChannelData = newBuffer.getChannelData(channel);

      // 선택 구간 이전 복사
      newChannelData.set(channelData.slice(0, startOffset), 0);
      // 선택 구간 이후 복사
      newChannelData.set(channelData.slice(endOffset), startOffset);
    }
    return newBuffer;
  };

  // 버퍼 뮤트 헬퍼 함수
  const muteBuffer = (
    buffer: AudioBuffer,
    startOffset: number,
    endOffset: number
  ): AudioBuffer => {
    const newBuffer = audioContext.current!.createBuffer(
      buffer.numberOfChannels,
      buffer.length,
      buffer.sampleRate
    );

    for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      const newChannelData = newBuffer.getChannelData(channel);
      newChannelData.set(channelData);
      newChannelData.fill(0, startOffset, endOffset);
    }
    return newBuffer;
  };

  // vocal과 mr 병합 헬퍼 함수
  const mergeVocalAndMr = async (
    vocalBuffer: AudioBuffer,
    mrBuffer: AudioBuffer
  ): Promise<AudioBuffer> => {
    const offlineCtx = new OfflineAudioContext(
      2,
      vocalBuffer.length,
      vocalBuffer.sampleRate
    );

    const vocalSource = offlineCtx.createBufferSource();
    const mrSource = offlineCtx.createBufferSource();

    vocalSource.buffer = vocalBuffer;
    mrSource.buffer = mrBuffer;

    const merger = offlineCtx.createChannelMerger(2);
    vocalSource.connect(merger, 0, 0);
    mrSource.connect(merger, 0, 1);
    merger.connect(offlineCtx.destination);

    vocalSource.start(0);
    mrSource.start(0);

    return await offlineCtx.startRendering();
  };

  const audioBufferToMp3 = (audioBuffer: AudioBuffer): Uint8Array => {
    const mp3encoder = new lamejs.Mp3Encoder(
      audioBuffer.numberOfChannels,
      audioBuffer.sampleRate,
      320
    );
    const mp3Data: Uint8Array[] = [];

    // MP3 인코더의 프레임 크기에 맞춤
    const sampleBlockSize = 1152; // MPEG-1 Layer 3 프레임당 샘플 수
    const channels = audioBuffer.numberOfChannels;

    try {
      // 스테레오/모노에 따라 다르게 처리
      if (channels === 2) {
        // 스테레오 처리
        const leftData = audioBuffer.getChannelData(0);
        const rightData = audioBuffer.getChannelData(1);
        const leftChunk = new Int16Array(sampleBlockSize);
        const rightChunk = new Int16Array(sampleBlockSize);

        for (let i = 0; i < audioBuffer.length; i += sampleBlockSize) {
          // 현재 청크의 실제 샘플 수 계산
          const currentBlockSize = Math.min(
            sampleBlockSize,
            audioBuffer.length - i
          );

          // 각 채널의 데이터를 개별적으로 처리
          for (let j = 0; j < currentBlockSize; j++) {
            leftChunk[j] = convert32to16(leftData[i + j]);
            rightChunk[j] = convert32to16(rightData[i + j]);
          }

          // 남은 샘플을 0으로 채움
          if (currentBlockSize < sampleBlockSize) {
            leftChunk.fill(0, currentBlockSize);
            rightChunk.fill(0, currentBlockSize);
          }

          const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
          if (mp3buf.length > 0) {
            mp3Data.push(new Uint8Array(mp3buf));
          }
        }
      } else {
        // 모노 처리
        const channelData = audioBuffer.getChannelData(0);
        const chunk = new Int16Array(sampleBlockSize);

        for (let i = 0; i < audioBuffer.length; i += sampleBlockSize) {
          const currentBlockSize = Math.min(
            sampleBlockSize,
            audioBuffer.length - i
          );

          for (let j = 0; j < currentBlockSize; j++) {
            chunk[j] = convert32to16(channelData[i + j]);
          }

          if (currentBlockSize < sampleBlockSize) {
            chunk.fill(0, currentBlockSize);
          }

          const mp3buf = mp3encoder.encodeBuffer(chunk);
          if (mp3buf.length > 0) {
            mp3Data.push(new Uint8Array(mp3buf));
          }
        }
      }

      // Finish encoding
      const end = mp3encoder.flush();
      if (end.length > 0) {
        mp3Data.push(new Uint8Array(end));
      }

      // Combine all chunks
      const totalLength = mp3Data.reduce((acc, buf) => acc + buf.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      for (const buf of mp3Data) {
        result.set(buf, offset);
        offset += buf.length;
      }

      return result;
    } catch (error) {
      console.error("MP3 encoding error:", error);
      throw error;
    }
  };

  // Float32 to Int16 변환 헬퍼 함수
  const convert32to16 = (sample: number): number => {
    // Float32 (-1.0 to +1.0) to Int16 (-32768 to +32767) 변환
    const s = Math.max(-1, Math.min(1, sample)); // 클리핑 방지
    return s < 0 ? Math.max(s * 0x8000, -0x8000) : Math.min(s * 0x7fff, 0x7fff);
  };
  return {
    currentAudio,
    handlePlayAudioPair,
    handleTimeUpdate,
    handleSkipTime,
    handleTrimAudio,
    mergeAudios,
  };
};
